import { triggerDoorConfiguration, updateConfigDoor } from '@gozoki/api';
import type { OpeningConfigLine } from '@gozoki/api-types';
import { Button, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { DateTime, type WeekdayNumbers } from 'luxon';
import { useState } from 'react';
import { useToast } from '../../components/communication/Toast';
import { getErrorMessageWithSentry } from '../../utils/sentry';
import { fonts } from '../../utils/theme/fonts.theme';
import { DayConfigCard } from './DayConfigCard';

export const OpeningHoursConfigPanel = ({
    storeId,
    openingConfigs,
    onDayConfigChange,
}: {
    storeId: number;
    openingConfigs: { [key: string]: OpeningConfigLine[] };
    onDayConfigChange: (dayNumber: number, configCards: OpeningConfigLine[]) => void;
}) => {
    const [Toast, showToast] = useToast();

    const { mutate: mutateTriggerDoorConfiguration, isPending: isSyncing } = useMutation({
        mutationFn: triggerDoorConfiguration,
        onSuccess: (response) => {
            showToast({
                message: response.message || 'Synchronisation des portes effectuée avec succès',
                severity: 'success',
            });
        },
        onError: (error) => {
            showToast({
                message: getErrorMessageWithSentry(error) || 'Erreur lors de la synchronisation',
                severity: 'error',
            });
        },
    });

    const { mutateAsync: mutateUpdateConfigDoorAsync } = useMutation({
        mutationFn: async () => {
            return updateConfigDoor(storeId, openingConfigs);
        },
    });

    const [isSaving, setIsSaving] = useState(false);

    const handleSaveAndSync = async () => {
        setIsSaving(true);
        try {
            // Save config
            const response = await mutateUpdateConfigDoorAsync();
            showToast({
                message: response.message,
                severity: 'success',
            });
            // Sync config
            mutateTriggerDoorConfiguration();
        } catch (error) {
            showToast({
                message: getErrorMessageWithSentry(error) || 'Erreur lors de la sauvegarde',
                severity: 'error',
            });
        } finally {
            setIsSaving(false);
        }
    };

    const isLoading = isSaving || isSyncing;

    const daysOfWeek = Array.from({ length: 7 }, (_, i) => {
        const date = DateTime.fromObject({ weekday: (i + 1) as WeekdayNumbers }).setLocale('fr');
        return { day: date.weekdayLong ?? '', dayNumber: date.weekday };
    });

    return (
        <div style={{ padding: '16px' }}>
            <Toast />
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '16px',
                }}
            >
                <Typography style={fonts.pageSubtitle} flexGrow={1}>
                    Horaires d'ouverture
                </Typography>
                <Button
                    variant="outlined"
                    onClick={handleSaveAndSync}
                    disabled={isLoading}
                    size="small"
                >
                    {isLoading
                        ? 'Enregistrement et synchronisation...'
                        : 'Enregistrer et synchroniser'}
                </Button>
            </div>
            {daysOfWeek.map((item) => (
                <DayConfigCard
                    key={item.dayNumber}
                    day={item.day}
                    openingConfigs={openingConfigs[item.dayNumber] || []}
                    onConfigChange={(configCards) => onDayConfigChange(item.dayNumber, configCards)}
                />
            ))}
        </div>
    );
};
